import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { assessmentAPI } from '../services/api';
import { TrendingUp, TrendingDown, Calendar, Download, BarChart3, LineChart as LineChartIcon } from 'lucide-react';
import WheelChart from '../components/WheelChart';
import { generateAssessmentPDF } from '../utils/pdfGenerator';

const ProgressDashboard = () => {
  const [assessments, setAssessments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [compareMode, setCompareMode] = useState(false);
  const [compareAssessments, setCompareAssessments] = useState([null, null]);
  const { toast } = useToast();

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isPremium = user.isPremium || false;

  useEffect(() => {
    if (!isPremium) {
      window.location.href = '/premium';
      return;
    }
    loadAssessments();
  }, [isPremium]);

  const loadAssessments = async () => {
    setIsLoading(true);
    try {
      const response = await assessmentAPI.getAll();
      setAssessments(response.assessments || []);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Could not load assessments',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const calculateProgress = () => {
    if (assessments.length < 2) return null;

    const latest = assessments[0];
    const oldest = assessments[assessments.length - 1];

    const latestAvg = latest.domains.reduce((sum, d) => sum + d.score, 0) / latest.domains.length;
    const oldestAvg = oldest.domains.reduce((sum, d) => sum + d.score, 0) / oldest.domains.length;
    const improvement = latestAvg - oldestAvg;

    // Find biggest improvement
    const improvements = latest.domains.map((domain, idx) => ({
      name: domain.name,
      change: domain.score - oldest.domains[idx].score
    }));

    const biggestImprovement = improvements.reduce((max, curr) => 
      curr.change > max.change ? curr : max
    );

    const biggestDecline = improvements.reduce((min, curr) => 
      curr.change < min.change ? curr : min
    );

    return {
      overall: improvement,
      biggestImprovement,
      biggestDecline,
      latestAvg: latestAvg.toFixed(1),
      oldestAvg: oldestAvg.toFixed(1)
    };
  };

  const handleDownloadAssessment = (assessment) => {
    try {
      generateAssessmentPDF(assessment.domains, user.name);
      toast({
        title: 'Download Complete',
        description: 'Assessment PDF downloaded successfully',
      });
    } catch (error) {
      toast({
        title: 'Download Failed',
        description: 'Could not generate PDF',
        variant: 'destructive',
      });
    }
  };

  const progress = calculateProgress();

  if (!isPremium) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center">
        <p className="text-gray-600">Loading your progress...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Progress Dashboard</h1>
          <p className="text-gray-600">Track your journey to a balanced life</p>
        </div>

        {/* Progress Overview */}
        {progress && (
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <Card className="p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-gray-600">Overall Progress</h3>
                {progress.overall >= 0 ? (
                  <TrendingUp className="w-5 h-5 text-green-600" />
                ) : (
                  <TrendingDown className="w-5 h-5 text-red-600" />
                )}
              </div>
              <p className="text-3xl font-bold text-gray-900">
                {progress.overall >= 0 ? '+' : ''}{progress.overall.toFixed(1)}
              </p>
              <p className="text-sm text-gray-600 mt-1">
                From {progress.oldestAvg} to {progress.latestAvg}
              </p>
            </Card>

            <Card className="p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-gray-600">Biggest Improvement</h3>
                <TrendingUp className="w-5 h-5 text-green-600" />
              </div>
              <p className="text-lg font-bold text-gray-900">{progress.biggestImprovement.name}</p>
              <p className="text-sm text-green-600 mt-1">
                +{progress.biggestImprovement.change.toFixed(1)} points
              </p>
            </Card>

            <Card className="p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-gray-600">Total Assessments</h3>
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <p className="text-3xl font-bold text-gray-900">{assessments.length}</p>
              <p className="text-sm text-gray-600 mt-1">Saved over time</p>
            </Card>
          </div>
        )}

        {/* Comparison Mode Toggle */}
        <div className="mb-6 flex gap-3">
          <Button
            onClick={() => setCompareMode(!compareMode)}
            variant={compareMode ? 'default' : 'outline'}
            className={compareMode ? 'bg-green-600 text-white' : ''}
          >
            <BarChart3 className="w-4 h-4 mr-2" />
            {compareMode ? 'Exit Comparison' : 'Compare Assessments'}
          </Button>
        </div>

        {/* Comparison View */}
        {compareMode && (
          <Card className="p-6 mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Compare Two Assessments</h3>
            <div className="grid md:grid-cols-2 gap-6">
              {[0, 1].map((idx) => (
                <div key={idx}>
                  <label className="text-sm font-semibold text-gray-700 mb-2 block">
                    Assessment {idx + 1}
                  </label>
                  <select
                    value={compareAssessments[idx]?.id || ''}
                    onChange={(e) => {
                      const selected = assessments.find(a => a.id === e.target.value);
                      const newCompare = [...compareAssessments];
                      newCompare[idx] = selected;
                      setCompareAssessments(newCompare);
                    }}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  >
                    <option value="">Select assessment...</option>
                    {assessments.map((assessment) => (
                      <option key={assessment.id} value={assessment.id}>
                        {new Date(assessment.createdAt).toLocaleDateString()}
                      </option>
                    ))}
                  </select>
                  {compareAssessments[idx] && (
                    <div className="mt-4">
                      <WheelChart 
                        domains={compareAssessments[idx].domains} 
                        editable={false}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>

            {compareAssessments[0] && compareAssessments[1] && (
              <div className="mt-6">
                <h4 className="font-semibold text-gray-900 mb-3">Changes:</h4>
                <div className="space-y-2">
                  {compareAssessments[0].domains.map((domain, idx) => {
                    const change = domain.score - compareAssessments[1].domains[idx].score;
                    return (
                      <div key={domain.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <span className="text-sm font-medium text-gray-700">{domain.name}</span>
                        <span className={`text-sm font-bold ${change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {change >= 0 ? '+' : ''}{change}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Assessment History */}
        <Card className="p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Assessment History</h3>
          
          {assessments.length === 0 ? (
            <div className="text-center py-12">
              <LineChartIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No saved assessments yet</p>
              <Button
                onClick={() => window.location.href = '/assessment'}
                className="mt-4 bg-green-600 text-white"
              >
                Take Your First Assessment
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {assessments.map((assessment) => {
                const avgScore = (assessment.domains.reduce((sum, d) => sum + d.score, 0) / assessment.domains.length).toFixed(1);
                
                return (
                  <div
                    key={assessment.id}
                    className="border-2 border-gray-200 rounded-lg p-4 hover:border-green-300 transition-all cursor-pointer"
                    onClick={() => setSelectedAssessment(selectedAssessment?.id === assessment.id ? null : assessment)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center space-x-3">
                          <Calendar className="w-5 h-5 text-gray-500" />
                          <span className="font-semibold text-gray-900">
                            {new Date(assessment.createdAt).toLocaleDateString('en-US', { 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            })}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1 ml-8">
                          Overall Score: <span className="font-bold text-green-600">{avgScore}/10</span>
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadAssessment(assessment);
                          }}
                        >
                          <Download className="w-4 h-4 mr-1" />
                          PDF
                        </Button>
                      </div>
                    </div>

                    {selectedAssessment?.id === assessment.id && (
                      <div className="mt-6 pt-6 border-t border-gray-200">
                        <div className="grid md:grid-cols-2 gap-6">
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-3">Scores:</h4>
                            <div className="space-y-2">
                              {assessment.domains.map((domain) => (
                                <div key={domain.id} className="flex items-center justify-between">
                                  <span className="text-sm text-gray-700">{domain.name}</span>
                                  <span className="font-bold text-gray-900">{domain.score}/10</span>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div>
                            <WheelChart domains={assessment.domains} editable={false} />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ProgressDashboard;
